import axios from 'axios'
import nacl from 'tweetnacl'
import naclUtil from 'tweetnacl-util'
import { baseUrl, config } from 'features/constants'
import { b64ToBytes, bytesToB64 } from 'services/e2ee/cryptoService'
import { fetchPublicKey } from 'services/e2ee/keyInit'
import { getPrivateKey } from 'services/e2ee/keyStore'

const api = `${baseUrl}/api/church-threads`
const cache = new Map()
const inflight = new Map()

const randomBytes = (length) => {
  const bytes = new Uint8Array(length)
  window.crypto.getRandomValues(bytes)
  return bytes
}

const cacheKey = (accountId, churchId, version) => `${accountId}:${churchId}:${version}`

export const clearChurchThreadGroupKeyCache = (accountId) => {
  if (!accountId) {
    cache.clear()
    inflight.clear()
    return
  }
  const prefix = `${accountId}:`
  Array.from(cache.keys()).forEach((key) => key.startsWith(prefix) && cache.delete(key))
  Array.from(inflight.keys()).forEach((key) => key.startsWith(prefix) && inflight.delete(key))
}

export const generateChurchThreadGroupKey = () => bytesToB64(randomBytes(nacl.secretbox.keyLength))

export const wrapChurchThreadGroupKeyForAccount = ({ groupKey, recipientPublicKey, senderPrivateKey }) => {
  const nonce = randomBytes(nacl.box.nonceLength)
  const ciphertext = nacl.box(
    b64ToBytes(groupKey),
    nonce,
    b64ToBytes(recipientPublicKey),
    b64ToBytes(senderPrivateKey),
  )
  return JSON.stringify({ algorithm: 'nacl.box', ciphertext: bytesToB64(ciphertext), nonce: bytesToB64(nonce) })
}

export const unwrapChurchThreadGroupKey = ({ encryptedGroupKey, senderPublicKey, recipientPrivateKey }) => {
  try {
    const payload = JSON.parse(encryptedGroupKey)
    if (payload?.algorithm !== 'nacl.box') return null
    const decrypted = nacl.box.open(
      b64ToBytes(payload.ciphertext),
      b64ToBytes(payload.nonce),
      b64ToBytes(senderPublicKey),
      b64ToBytes(recipientPrivateKey),
    )
    return decrypted ? bytesToB64(decrypted) : null
  } catch {
    return null
  }
}

export const encryptChurchThreadText = ({ plaintext, groupKey }) => {
  const iv = randomBytes(nacl.secretbox.nonceLength)
  const ciphertext = nacl.secretbox(naclUtil.decodeUTF8(plaintext), iv, b64ToBytes(groupKey))
  return { ciphertext: bytesToB64(ciphertext), iv: bytesToB64(iv) }
}

export const decryptChurchThreadText = ({ ciphertext, iv, groupKey }) => {
  try {
    const decrypted = nacl.secretbox.open(b64ToBytes(ciphertext), b64ToBytes(iv), b64ToBytes(groupKey))
    return decrypted ? naclUtil.encodeUTF8(decrypted) : null
  } catch {
    return null
  }
}

export const fetchChurchThreadEncryptionBootstrap = async ({ churchId, token }) => {
  const { data } = await axios.get(`${api}/${churchId}/encryption/bootstrap`, config(token))
  return data.bootstrap
}

export const fetchOwnChurchThreadWrappedKey = async ({ churchId, keyVersion, token }) => {
  const { data } = await axios.get(`${api}/${churchId}/encryption/versions/${keyVersion}/key`, config(token))
  return data.wrappedKey
}

export const fetchChurchThreadRotationState = async ({ churchId, token }) => {
  const { data } = await axios.get(`${api}/${churchId}/encryption/rotation`, config(token))
  return data.rotation
}

export const getChurchThreadGroupKey = async ({ churchId, keyVersion, token, accountId, churchPublicKey }) => {
  const version = Number(keyVersion)
  if (!Number.isInteger(version) || version < 1) throw new Error('Invalid Church Thread key version.')
  const key = cacheKey(accountId, churchId, version)
  if (cache.has(key)) return cache.get(key)
  if (inflight.has(key)) return inflight.get(key)

  const request = (async () => {
    const privateKey = getPrivateKey(accountId)
    if (!privateKey) throw new Error('Encryption key is not available on this browser.')
    const [wrappedKey, senderPublicKey] = await Promise.all([
      fetchOwnChurchThreadWrappedKey({ churchId, keyVersion: version, token }),
      churchPublicKey ? Promise.resolve(churchPublicKey) : fetchPublicKey(churchId, token),
    ])
    if (!senderPublicKey) throw new Error('Church encryption public key is not available.')
    const groupKey = unwrapChurchThreadGroupKey({
      encryptedGroupKey: wrappedKey.encryptedGroupKey,
      senderPublicKey,
      recipientPrivateKey: privateKey,
    })
    if (!groupKey) throw new Error('Unable to unlock Church Thread key.')
    cache.set(key, groupKey)
    return groupKey
  })()

  inflight.set(key, request)
  try {
    return await request
  } finally {
    inflight.delete(key)
  }
}

export const fetchChurchThreadMessages = async ({ churchId, token, cursor, limit = 40 }) => {
  const params = new URLSearchParams({ limit: String(limit) })
  if (cursor) params.set('cursor', cursor)
  const { data } = await axios.get(`${api}/${churchId}/messages?${params.toString()}`, config(token))
  return data
}

export const decryptChurchThreadMessages = async ({ messages, token, accountId, churchPublicKey }) => Promise.all(
  messages.map(async (message) => {
    try {
      const groupKey = await getChurchThreadGroupKey({
        churchId: message.churchId,
        keyVersion: message.keyVersion,
        token,
        accountId,
        churchPublicKey,
      })
      const text = decryptChurchThreadText({ ciphertext: message.ciphertext, iv: message.iv, groupKey })
      if (!text) throw new Error('Unable to decrypt')
      return { ...message, text, decryptStatus: 'decrypted' }
    } catch {
      return { ...message, text: 'Unable to decrypt', decryptStatus: 'unavailable' }
    }
  }),
)

export const sendEncryptedChurchThreadText = async ({ churchId, token, accountId, currentKeyVersion, plaintext, churchPublicKey }) => {
  const keyVersion = Number(currentKeyVersion)
  const groupKey = await getChurchThreadGroupKey({ churchId, keyVersion, token, accountId, churchPublicKey })
  const encrypted = encryptChurchThreadText({ plaintext, groupKey })
  const { data } = await axios.post(`${api}/${churchId}/messages`, { ...encrypted, keyVersion }, config(token))
  return data
}

const createKeyPackage = ({ recipients, senderPrivateKey, groupKey }) => ({
  keys: recipients.filter((recipient) => recipient.publicKey).map((recipient) => ({
    recipientType: recipient.recipientType,
    recipientId: recipient.recipientId,
    encryptedGroupKey: wrapChurchThreadGroupKeyForAccount({
      groupKey,
      recipientPublicKey: recipient.publicKey,
      senderPrivateKey,
    }),
  })),
})

export const bootstrapInitialChurchThreadKeyVersion = async ({ churchId, token, accountId }) => {
  const bootstrap = await fetchChurchThreadEncryptionBootstrap({ churchId, token })
  if (bootstrap.keyVersion.hasWrappedKeys) return { status: 'already_initialized', bootstrap }
  const senderPrivateKey = getPrivateKey(accountId)
  if (!senderPrivateKey) throw new Error('Restore your encryption key to initialize this Church Thread.')
  const groupKey = generateChurchThreadGroupKey()
  const keyPackage = createKeyPackage({ recipients: bootstrap.recipients, senderPrivateKey, groupKey })
  if (!keyPackage.keys.length) return { status: 'no_recipients_with_public_keys', bootstrap }
  const { data } = await axios.post(
    `${api}/${churchId}/encryption/versions/1/keys`,
    { keyVersion: 1, keys: keyPackage.keys },
    config(token),
  )
  cache.set(cacheKey(accountId, churchId, 1), groupKey)
  return { status: 'uploaded', bootstrap, uploadResult: data }
}

export const rotateChurchThreadKeyIfRequired = async ({ churchId, token, accountId }) => {
  const rotation = await fetchChurchThreadRotationState({ churchId, token })
  if (!rotation.rotationRequired) return { status: 'not_required', rotation }
  if (!rotation.canRotate || !rotation.recipients?.length) return { status: 'blocked', rotation }
  const senderPrivateKey = getPrivateKey(accountId)
  if (!senderPrivateKey) throw new Error('Encryption key is not available on this browser.')
  const groupKey = generateChurchThreadGroupKey()
  const keyPackage = createKeyPackage({ recipients: rotation.recipients, senderPrivateKey, groupKey })
  const { data } = await axios.post(`${api}/${churchId}/encryption/rotation`, {
    expectedCurrentKeyVersion: rotation.currentKeyVersion,
    nextVersion: rotation.nextKeyVersion,
    reason: rotation.reason,
    keys: keyPackage.keys,
  }, config(token))
  cache.set(cacheKey(accountId, churchId, rotation.nextKeyVersion), groupKey)
  return { status: 'rotated', rotation, commitResult: data }
}

export const updateChurchThreadPostingEnabled = async ({ churchId, token, postingEnabled }) => {
  const { data } = await axios.patch(`${api}/${churchId}/posting`, { postingEnabled }, config(token))
  return data
}
